import { compact, every, map } from 'lodash';
import { epoch, formatDate, formatEpochAsDate, future, tz, utc } from '../../util/datetime';
import { extractExcerpt, replaceTags, sanitizeHtml, toUUID } from '../../util/transform';
import { DocType } from '../global/Doc.enum';
import { TimeUnitType } from '../global/TimeUnit.enum';
import Root from '../Root';
import { JobProgressType } from './Job.enum';
import type { IJob } from './Job.interface';
import JobItem from './JobItem';

export default class Job extends Root implements IJob {
  idWorkflow: string;
  idTicket: string;
  idContact: string;
  idAssignedTo: string;
  idOutlook: string;
  idGoogle: string;
  idLabels: Array<string>;
  jobTemplate: any;
  jobProgress: JobProgressType;
  absoluteDueDate: number;
  relativeDueDate: number;
  relativeInterval: TimeUnitType;
  isDueDateAbsolute: boolean;
  recurrence: number;
  recurrenceInterval: TimeUnitType;
  valueMonetary: number;
  valueMonetaryCurrency: string;
  valueTime: number;
  valueTimeUnit: TimeUnitType;
  repeat: {
    repeats: number,
    repeatEvery: number,
    repeatOn: Array<number>,
    repeatBy: false,
    duration: {
      occurrence: number,
      endOption: number,
      startDate: number,
      endDate: number
    }
  };

  name: string;
  description: string;
  items: Array<JobItem>;
  isDueAhead: boolean;
  isDue: boolean;
  isOverdue: boolean;
  isRecurring: boolean;
  isCompleted: boolean;
  isPinned: boolean;

  getName() {
    return this.name;
  }

  getDescription() {
    return this.description;
  }

  getExcerpt() {
    return extractExcerpt(replaceTags(this.getDescription(), ' '));
  }

  getIsImportant() {
    return this.isPinned;
  }

  getProgressName() {
    return this.jobProgress.name;
  }

  getDueDate(timeZone, locale, localeFormat) {
    return this.isDueDateAbsolute
      ? this.getAbsoluteDueDate(timeZone, locale, localeFormat)
      : this.getRelativeDueDate(timeZone, locale, localeFormat);
  }

  getDateSort() {
    return this.isDueDateAbsolute
      ? this.absoluteDueDate
      : epoch(future(utc(), this.relativeInterval.name, this.relativeDueDate));
  }

  getAbsoluteDueDate(timeZone, locale, localeFormat) {
    return formatEpochAsDate(this.absoluteDueDate, timeZone, locale, localeFormat);
  }

  getRelativeDueDate(timeZone, locale, localeFormat) {
    return formatDate(
      tz(
        future(utc(this.timeStamp), this.relativeInterval.name, this.relativeDueDate),
        timeZone
      ),
      locale,
      localeFormat
    );
  }

  showRelativeDueDateSample(timeZone, locale, localeFormat) {
    return formatDate(
      tz(future(utc(), this.relativeInterval.name, this.relativeDueDate), timeZone),
      locale,
      localeFormat
    );
  }

  isJobProgressDoing() {
    return this.jobProgress === JobProgressType.doing;
  }

  isJobDue() {
    return this.isDue;
  }

  isJobOverdue() {
    return this.isOverdue;
  }

  isJobCompleted() {
    return this.isCompleted;
  }

  canBeCompleted() {
    return every(this.items, { isActive: true });
  }

  canBeMoved() {
    return this.jobProgress === JobProgressType.done && !this.canBeCompleted();
  }

  static availableRepeats = () => [
    { id: 1, value: 'Daily' },
    { id: 2, value: 'Weekly' },
    { id: 3, value: 'Monthly' },
    { id: 4, value: 'Yearly' }
  ];

  static availableDaysOfTheWeek = () => [
    { id: 1, value: 'Monday', weight: 1 },
    { id: 2, value: 'Tuesday', weight: 2 },
    { id: 3, value: 'Wednesday', weight: 3 },
    { id: 4, value: 'Thursday', weight: 4 },
    { id: 5, value: 'Friday', weight: 5 },
    { id: 6, value: 'Saturday', weight: 6 },
    { id: 0, value: 'Sunday', weight: 7 }
  ];

  static availableEndOptions = () => [
    { id: 1, value: 'Never' },
    { id: 2, value: 'After' },
    { id: 3, value: 'Date' }
  ];

  static availableRepeatEvery = () => [...Array(30 + 1).keys()].slice(1);

  static build = (data: IJob) => new Job(data);
  static blank = () => new Job({ isDueDateAbsolute: true, isActive: true });

  /**
   * Constructor
   * @param {IJob} data
   * @param {DocType} docType
   */
  constructor(data?: IJob, docType?: DocType) {
    super(docType || DocType.of('__datum.job'), data);

    this.idWorkflow = toUUID(data?.idWorkflow);
    this.idTicket = toUUID(data?.idTicket);
    this.idContact = toUUID(data?.idContact);
    this.idAssignedTo = toUUID(data?.idAssignedTo);
    this.idOutlook = data?.idOutlook || '';
    this.idGoogle = data?.idGoogle || '';
    this.idLabels = compact(map(data?.idLabels, (obj) => toUUID(obj)));
    this.jobTemplate = data?.jobTemplate || undefined;
    // TODO: data?.jobProgress is legacy data structure
    this.jobProgress = JobProgressType.of(data?.jobProgress?.name || data?.jobProgress);
    // TODO: data?.jobProgress is legacy data structure
    this.absoluteDueDate = data?.absoluteDueDate
      ? epoch(utc(data?.absoluteDueDate))
      : epoch(future(utc(), 'days', 7));
    this.relativeDueDate = data?.relativeDueDate ? epoch(utc(data?.relativeDueDate)) : 7;
    // TODO: data?.relativeInterval is legacy data structure
    this.relativeInterval = TimeUnitType.of(
      data?.relativeInterval?.id || data?.relativeInterval
    );
    // TODO: data?.relativeInterval is legacy data structure
    this.isDueDateAbsolute = data?.isDueDateAbsolute === true;
    this.recurrence = parseInt(data?.recurrence || 0, 10);
    // TODO: data?.reccurenceInterval is legacy data structure
    this.recurrenceInterval = TimeUnitType.of(
      data?.recurrenceInterval?.id || data?.reccurenceInterval
    );
    // TODO: data?.reccurenceInterval is legacy data structure
    this.valueMonetary = parseInt(data?.valueMonetary || 0, 10);
    // TODO: data?.jobProgress is legacy data structure
    this.valueMonetaryCurrency =
      data?.valueMonetaryCurrency?.symbol || data?.valueMonetaryCurrency;
    // TODO: data?.jobProgress is legacy data structure
    this.valueTime = parseInt(data?.valueTime || 0, 10);
    // TODO: data?.valueTimeUnit is legacy data structure
    this.valueTimeUnit = TimeUnitType.of(data?.valueTimeUnit?.id || data?.valueTimeUnit);
    // TODO: data?.valueTimeUnit is legacy data structure
    this.repeat = {
      repeats: parseInt(data?.repeat?.repeats || 2, 10),
      repeatEvery: parseInt(data?.repeat?.repeatEvery || 1, 10),
      repeatOn: map(data?.repeat?.repeatOn, (obj) => parseInt(obj || 0, 10)),
      repeatBy: data?.repeat?.repeatBy === true,
      duration: {
        occurrence: parseInt(data?.repeat?.duration?.occurrence || 0, 10),
        endOption: parseInt(data?.repeat?.duration?.endOption || 1, 10),
        startDate: data?.repeat?.duration?.startDate
          ? epoch(utc(data?.repeat?.duration?.startDate))
          : 0,
        endDate: data?.repeat?.duration?.endDate
          ? epoch(utc(data?.repeat?.duration?.endDate))
          : 0
      }
    };
    this.name = data?.name || '';
    this.description = sanitizeHtml(data?.description || '');
    // TODO: data?.subItems is legacy data structure
    this.items = map(data?.items || data?.subItems, (obj) => new JobItem(obj));
    // TODO: data?.subItems is legacy data structure
    this.isDueAhead = data?.isDueAhead === true;
    this.isDue = data?.isDue === true;
    this.isOverdue = data?.isOverdue === true;
    this.isRecurring = data?.isRecurring === true;
    this.isCompleted = data?.isCompleted === true;
    this.isPinned = data?.isPinned === true;
  }
}
